import { CartAddEvent } from '@theme/events';

/**
 * MC Cart Upsell - Add-ons block in the cart drawer + cart page.
 *
 * Two-tier section: top add-ons favor Shopify complementary recommendations,
 * then broader add-ons fall back to related recommendations and curated refills.
 *
 * The whole component hides when the section has no cards (empty cart,
 * empty recs, or all recs already in cart).
 */
(function () {
  if (window.customElements.get('mc-cart-upsell')) return;

  class McCartUpsell extends HTMLElement {
    #controller = null;
    #lastSeedId = null;
    #pending = false;
    #debounceTimer = null;
    #cachedCart = null;
    #cachedAt = 0;
    #queuedRefreshCart = null;
    #needsRefresh = false;
    #fallbackCache = new Map();
    #variantCache = new Map();
    #restoredFromSnapshot = false;
    #stableAddedProductIds = new Set();
    #isLoading = false;
    #loadingTimer = null;

    connectedCallback() {
      this.limit = parseInt(this.dataset.limit || '8', 10);
      const configuredIntent = this.dataset.intent || 'complementary';
      this.intent = configuredIntent;
      this.recommendationIntents = ['complementary', configuredIntent, 'related']
        .filter(Boolean)
        .filter((intent, index, intents) => intents.indexOf(intent) === index)
        .slice(0, 2);
      this.fallbackHandles = (this.dataset.fallbackCollections || this.dataset.fallbackCollection || 'refills')
        .split(',')
        .map(handle => handle.trim())
        .filter(Boolean);

      this.section = this.querySelector('[data-mcu-section="addons"]');
      this.featuredEl = this.querySelector('[data-mcu-featured]');
      this.featuredListEl = this.querySelector('[data-mcu-featured-list]');
      this.moreEl = this.querySelector('[data-mcu-more]');
      this.listEl = this.querySelector('[data-mcu-list]');
      this.loadingEl = this.querySelector('[data-mcu-loading]');
      this.navEl = this.querySelector('[data-mcu-nav]');
      this.prevBtn = this.querySelector('[data-mcu-prev]');
      this.nextBtn = this.querySelector('[data-mcu-next]');

      if (!this.section || !this.featuredListEl || !this.listEl) {
        this.#hide();
        return;
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.#scrollByCards(-2));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.#scrollByCards(2));
      if (this.listEl) {
        this.listEl.addEventListener('scroll', () => this.#updateNav(), { passive: true });
      }

      this.boundResize = () => this.#updateNav();
      window.addEventListener('resize', this.boundResize, { passive: true });

      this.#hydrateStableAddedIds();
      this.#restoredFromSnapshot = this.#restoreFromSnapshot();
      this.#scheduleRefresh(null, this.#restoredFromSnapshot ? 1400 : 250);

      // Event listeners pass any cart payload from the event detail so we can
      // skip the /cart.js fetch when the dispatcher already has the cart state.
      this.boundRefresh = (e) => {
        if ((e?.detail?.source || e?.detail?.data?.source) === 'mc-cart-upsell' && !(e?.detail?.cart || e?.detail?.data?.cart)) {
          return;
        }
        this.#scheduleRefresh(e?.detail?.cart || e?.detail?.data?.cart || null);
      };
      document.addEventListener('cart:updated', this.boundRefresh);
      document.addEventListener('cart:items-added', this.boundRefresh);
      document.addEventListener('cart:update', this.boundRefresh);
    }

    disconnectedCallback() {
      this.#controller?.abort();
      clearTimeout(this.#debounceTimer);
      clearTimeout(this.#loadingTimer);
      document.removeEventListener('cart:updated', this.boundRefresh);
      document.removeEventListener('cart:items-added', this.boundRefresh);
      document.removeEventListener('cart:update', this.boundRefresh);
      window.removeEventListener('resize', this.boundResize);
    }

    // Debounce burst cart events so we don't fire multiple /cart.js fetches
    // when a single add-to-cart action dispatches several events in a row.
    #scheduleRefresh(detailCart, delay = 250) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = setTimeout(() => this.#refresh(detailCart), delay);
    }

    async #refresh(detailCart) {
      if (this.#pending) {
        this.#queuedRefreshCart = detailCart || this.#queuedRefreshCart;
        this.#needsRefresh = true;
        return;
      }
      this.#pending = true;
      try {
        let cart = detailCart;
        // Use cached cart if it's fresh enough (cuts repeated /cart.js spam).
        if (!cart && this.#cachedCart && Date.now() - this.#cachedAt < 2000) {
          cart = this.#cachedCart;
        }
        if (!cart) {
          cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());
          this.#cachedCart = cart;
          this.#cachedAt = Date.now();
        } else {
          this.#cachedCart = cart;
          this.#cachedAt = Date.now();
        }
        if (!cart || !cart.items || cart.items.length === 0) {
          this.#hide();
          return;
        }

        const seedId = cart.items[0].product_id;
        if (seedId === this.#lastSeedId && this.#hasCards()) {
          // Re-filter in case cart membership changed.
          this.#filterByCart(cart);
        } else {
          this.#lastSeedId = seedId;
          await this.#loadAddons(seedId, cart);
        }

        this.#syncVisibility();
        requestAnimationFrame(() => requestAnimationFrame(() => this.#updateNav()));
      } catch (e) {
        this.#hide();
      } finally {
        this.#pending = false;
        if (this.#needsRefresh) {
          const queuedCart = this.#queuedRefreshCart;
          this.#queuedRefreshCart = null;
          this.#needsRefresh = false;
          this.#scheduleRefresh(queuedCart);
        }
      }
    }

    async #loadAddons(productId, cart) {
      if (!this.featuredListEl || !this.listEl) return;
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.#showLoading(this.#hasCards() ? 120 : 0);

      try {
        const inCartProductIds = new Set(cart.items.map(i => i.product_id));
        const [recommended, fallback] = await Promise.all([
          this.#loadRecommendations(productId),
          this.#loadFallbackProducts(inCartProductIds)
        ]);
        const roughProducts = this.#selectAddons(
          [...recommended, ...fallback],
          cart,
          Math.min(this.limit + 10, 18)
        );
        const products = this.#selectAddons(
          await this.#enrichVariantData(roughProducts),
          cart
        );

        this.#renderCards(products);
      } catch (e) {
        if (e.name !== 'AbortError' && !this.#hasCards()) {
          this.featuredListEl.innerHTML = '';
          this.listEl.innerHTML = '';
        }
      } finally {
        this.#hideLoading();
      }
    }

    async #loadRecommendations(productId) {
      const requests = this.recommendationIntents.map((intent, intentIndex) => {
        const url = new URL('/recommendations/products.json', window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('intent', intent);
        url.searchParams.set('limit', String(Math.min(this.limit + 4, 10)));
        return fetch(url.toString(), { signal: this.#controller.signal })
          .then(res => (res.ok ? res.json() : { products: [] }))
          .then(data => (data.products || [])
            .filter(p => p.available)
            .map((product, index) => ({
              ...product,
              _mcuSource: intent,
              _mcuIndex: (intentIndex * 100) + index
            })))
          .catch(e => {
            if (e.name === 'AbortError') throw e;
            return [];
          });
      });

      return (await Promise.all(requests)).flat();
    }

    async #loadFallbackProducts(excludeSet) {
      const products = [];
      for (const handle of this.fallbackHandles.slice(0, 2)) {
        const cached = this.#fallbackCache.get(handle);
        if (cached) {
          products.push(...cached);
          continue;
        }

        try {
          const res = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + Math.min(this.limit + 8, 24), {
            signal: this.#controller.signal
          });
          if (!res.ok) continue;
          const data = await res.json();
          const collectionProducts = (data.products || [])
            .filter(p => p.available && !excludeSet.has(p.id))
            .map((product, index) => ({
              ...product,
              _mcuSource: 'fallback',
              _mcuFallbackHandle: handle,
              _mcuIndex: index
            }));
          this.#fallbackCache.set(handle, collectionProducts);
          products.push(...collectionProducts);
        } catch (e) {
          if (e.name === 'AbortError') throw e;
        }
      }
      return products;
    }

    #selectAddons(products, cart, limit = this.limit) {
      const inCartProductIds = new Set(cart.items.map(i => i.product_id));
      const scoredByProduct = new Map();

      for (const product of products) {
        if (!product?.available || inCartProductIds.has(product.id)) continue;
        const variant = this.#getVariant(product);
        if (!variant) continue;
        const scoredProduct = {
          product,
          score: this.#scoreProduct(product, variant),
        };
        const current = scoredByProduct.get(product.id);
        if (!current || scoredProduct.score > current.score) {
          scoredByProduct.set(product.id, scoredProduct);
        }
      }

      const scored = Array.from(scoredByProduct.values());
      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const sourceDelta = this.#sourceRank(a.product._mcuSource) - this.#sourceRank(b.product._mcuSource);
        if (sourceDelta !== 0) return sourceDelta;
        return (a.product._mcuIndex || 0) - (b.product._mcuIndex || 0);
      });

      const preferred = scored.filter(item => item.score >= 0);
      const pool = preferred.length >= Math.min(4, limit) ? preferred : scored;
      return pool.slice(0, limit).map(item => item.product);
    }

    #scoreProduct(product, variant) {
      const text = [
        product.title,
        product.handle,
        product.product_type,
        product.type,
        product.vendor
      ].filter(Boolean).join(' ').toLowerCase();
      const price = this.#variantPriceCents(variant);
      const isRefillLike = /refill|replacement|supply|supplies|tourniquet|chest seal|hyfin|bandage|dressing|gauze|hemostatic|quikclot|glove|shear|tape|splint|airway|mask|cpr|burn|waterjel|blanket|saline|pad|compress/.test(text);
      const isFullKitLike = /fully stocked|first responder|trauma kit|medical kit|ifak kit|bleeding control kit|oxygen kit|emt|ems/.test(text);
      const isContainerLike = /\bbag\b|backpack|pouch|case|pack/.test(text);

      let score = this.#sourceScore(product._mcuSource);
      if (isRefillLike) score += 38;
      if (/refill|replacement/.test(text)) score += 20;
      if (/tourniquet|chest seal|hyfin|quikclot|hemostatic/.test(text)) score += 16;
      if (price > 0 && price <= 5000) score += 14;
      else if (price > 0 && price <= 10000) score += 8;
      else if (price >= 20000) score -= 20;
      if (isFullKitLike && !isRefillLike) score -= 110;
      if (isContainerLike && !isRefillLike) score -= 55;
      if (/\bkit\b/.test(text) && !isRefillLike) score -= 45;
      if (/empty|non-stocked|pouch only/.test(text)) score -= 60;
      return score;
    }

    #renderCards(products) {
      if (!this.featuredListEl || !this.listEl) return;
      const featuredProducts = products.slice(0, 2);
      const moreProducts = products.slice(2);
      const frag = document.createDocumentFragment();
      for (const p of featuredProducts) {
        const card = this.#createCard(p, true);
        if (card) frag.appendChild(card);
      }
      this.featuredListEl.innerHTML = '';
      this.featuredListEl.appendChild(frag);

      const moreFrag = document.createDocumentFragment();
      for (const p of moreProducts) {
        const card = this.#createCard(p, false);
        if (card) moreFrag.appendChild(card);
      }
      this.listEl.innerHTML = '';
      this.listEl.appendChild(moreFrag);
      this.#applyStableAddedState();
      this.#syncVisibility();
    }

    #filterByCart(cart) {
      if (!this.featuredListEl || !this.listEl) return;
      const inCart = new Set(cart.items.map(i => i.product_id));
      const cards = this.querySelectorAll('[data-mcu-card]');
      cards.forEach(card => {
        const pid = parseInt(card.dataset.productId, 10);
        const stableId = String(pid);
        if (inCart.has(pid)) {
          if (this.#stableAddedProductIds.has(stableId) || card.classList.contains('mc-cart-upsell__card--added')) {
            const btn = card.querySelector('.mc-cart-upsell__add');
            this.#markCardAdded(card, btn, { persist: true });
            return;
          }
          this.#removeCard(card, false);
          return;
        }

        if (this.#stableAddedProductIds.has(stableId)) {
          this.#forgetStableAddedProduct(pid);
          this.#unmarkCardAdded(card);
        }
      });
      this.#syncVisibility();
    }

    async #handleAdd(e, { id, source, stable = false }) {
      const btn = e.currentTarget;
      if (btn.disabled) return;
      const card = btn.closest('[data-mcu-card]');
      const productId = card?.dataset.productId || '';
      const originalButtonHTML = btn.innerHTML;
      btn.disabled = true;
      btn.classList.add('mc-cart-upsell__add--pending');
      try {
        const sectionIds = this.#getCartSectionIds();
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: id,
              quantity: 1,
              properties: { '_upsell_source': source || 'cart-drawer' }
            }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });
        if (!res.ok) throw new Error('Add HTTP ' + res.status);
        const data = await res.json();
        if (data.status) throw new Error(data.message || 'Cart add failed');
        const itemCount = this.#getItemCountFromSections(data.sections) ?? this.#readCartBubbleCount() + 1;

        if (stable) {
          this.#updateCartBubble(itemCount);
          this.#patchCartSections(data.sections);
          this.#markCardAdded(card, btn, { persist: true });
          this.#saveSnapshot(productId, { persistAdded: true });
          this.#fetchCart().then(cart => {
            if (!cart) return;
            this.#cachedCart = cart;
            this.#cachedAt = Date.now();
          });
          return;
        }

        this.#saveSnapshot(productId);

        this.dispatchEvent(new CartAddEvent(data, 'mc-cart-upsell', {
          source: 'mc-cart-upsell',
          itemCount,
          variantId: String(id),
          sections: data.sections || {}
        }));

        this.#fetchCart().then(cart => {
          if (!cart) return;
          this.#cachedCart = cart;
          this.#cachedAt = Date.now();
          const legacyDetail = { ...data, cart, source: 'mc-cart-upsell' };
          document.dispatchEvent(new CustomEvent('cart:items-added', { detail: legacyDetail, bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: legacyDetail, bubbles: true }));
          this.#scheduleRefresh(cart, 900);
        });

        btn.classList.remove('mc-cart-upsell__add--pending');
        btn.classList.add('mc-cart-upsell__add--success');
        this.#setButtonIcon(btn, 'success');
        setTimeout(() => {
          if (card) this.#removeCard(card, true);
        }, 300);
      } catch {
        btn.disabled = false;
        btn.innerHTML = originalButtonHTML;
        btn.classList.remove('mc-cart-upsell__add--pending');
        btn.classList.add('mc-cart-upsell__add--error');
        setTimeout(() => btn.classList.remove('mc-cart-upsell__add--error'), 1500);
      }
    }

    #markCardAdded(card, btn, options = {}) {
      if (!(card instanceof HTMLElement)) return;
      if (options.persist) this.#rememberStableAddedProduct(card.dataset.productId);

      if (btn instanceof HTMLElement) {
        btn.classList.remove('mc-cart-upsell__add--pending');
        btn.classList.add('mc-cart-upsell__add--success');
        btn.disabled = true;
        btn.setAttribute('aria-label', 'Added to cart');
        this.#setButtonIcon(btn, 'success');
      }
      card.classList.add('mc-cart-upsell__card--added');
      card.querySelector('[data-mcu-variant-select]')?.setAttribute('disabled', '');

      const footer = card.querySelector('.mc-cart-upsell__footer');
      if (footer && !footer.querySelector('[data-mcu-added-label]')) {
        const label = document.createElement('span');
        label.className = 'mc-cart-upsell__added';
        label.dataset.mcuAddedLabel = '';
        label.textContent = 'Added';
        footer.insertBefore(label, btn);
      }
    }

    #unmarkCardAdded(card) {
      if (!(card instanceof HTMLElement)) return;
      const btn = card.querySelector('.mc-cart-upsell__add');
      card.classList.remove('mc-cart-upsell__card--added');
      card.querySelector('[data-mcu-variant-select]')?.removeAttribute('disabled');
      card.querySelector('[data-mcu-added-label]')?.remove();
      if (btn instanceof HTMLElement) {
        btn.disabled = false;
        btn.classList.remove('mc-cart-upsell__add--success', 'mc-cart-upsell__add--pending');
        btn.setAttribute('aria-label', btn.getAttribute('aria-label')?.replace(/^Added to cart$/i, 'Add to cart') || 'Add to cart');
        this.#setButtonIcon(btn, 'add');
      }
    }

    #updateCartBubble(itemCount) {
      document.querySelectorAll('cart-icon').forEach((icon) => {
        if (typeof icon.renderCartBubble === 'function') {
          icon.renderCartBubble(itemCount, false);
          return;
        }

        const countEl = icon.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count');
        const bubble = icon.querySelector('[ref="cartBubble"], .cart-bubble');
        if (countEl) {
          countEl.textContent = itemCount < 100 ? String(itemCount) : '';
          countEl.classList.toggle('hidden', itemCount === 0);
        }
        if (bubble) bubble.classList.toggle('visually-hidden', itemCount === 0);
      });
    }

    #patchCartSections(sections) {
      if (!sections) return;
      const component = this.closest('cart-items-component');
      if (!component) return;

      for (const html of Object.values(sections)) {
        if (typeof html !== 'string') continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const nextForm = doc.querySelector('.cart-form');
        const currentForm = component.querySelector('.cart-form');
        if (nextForm && currentForm) {
          currentForm.replaceWith(nextForm);
        }

        const nextSummary = doc.querySelector('.cart-drawer__summary');
        const currentSummary = component.querySelector('.cart-drawer__summary');
        if (nextSummary && currentSummary) {
          currentSummary.innerHTML = nextSummary.innerHTML;
        }

        if (nextForm || nextSummary) break;
      }
    }

    #syncVisibility() {
      const hasFeatured = this.featuredListEl && this.featuredListEl.children.length > 0;
      const hasMore = this.listEl && this.listEl.children.length > 0;
      const hasCards = hasFeatured || hasMore;
      const isLoading = this.#isLoading;
      if (this.featuredEl) this.featuredEl.toggleAttribute('hidden', !hasFeatured);
      if (this.moreEl) this.moreEl.toggleAttribute('hidden', !hasMore);
      if (this.section) this.section.toggleAttribute('hidden', !hasCards && !isLoading);
      if (hasCards || isLoading) this.#show();
      else this.#hide();
    }

    #showLoading(delay = 120) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = setTimeout(() => {
        this.#setLoading(true);
      }, delay);
    }

    #hideLoading() {
      clearTimeout(this.#loadingTimer);
      this.#setLoading(false);
    }

    #setLoading(isLoading) {
      this.#isLoading = Boolean(isLoading);
      this.toggleAttribute('aria-busy', this.#isLoading);
      if (this.loadingEl) {
        this.loadingEl.toggleAttribute('hidden', !this.#isLoading);
      }
      this.#syncVisibility();
    }

    #getCartSectionIds() {
      const ids = new Set();
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          ids.add(item.dataset.sectionId);
        }
      });
      return Array.from(ids);
    }

    async #fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    }

    #getItemCountFromSections(sections) {
      if (!sections) return null;
      for (const html of Object.values(sections)) {
        if (typeof html !== 'string') continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const hiddenCount = doc.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
        if (hiddenCount) return parseInt(hiddenCount, 10) || 0;
        const metaCount = doc.querySelector('.cart-drawer__heading-meta-count')?.textContent || '';
        const countMatch = metaCount.match(/\d+/);
        if (countMatch) return parseInt(countMatch[0], 10) || 0;
      }
      return null;
    }

    #readCartBubbleCount() {
      const countText = document.querySelector('cart-icon [ref="cartBubbleCount"], cart-icon .cart-bubble__text-count')?.textContent || '0';
      return parseInt(countText, 10) || 0;
    }

    #getVariant(product) {
      const variants = this.#getVisibleVariants(product);
      return variants.find(v => v.available) || variants[0] || null;
    }

    #getVisibleVariants(product) {
      const variants = product?._mcuVariants || product?.variants || [];
      return variants.filter(variant => !this.#isHiddenVariant(variant));
    }

    #isHiddenVariant(variant) {
      if (!variant) return true;
      const raw = variant._mcuHidden ?? variant.hidden ?? variant.hidden_variant ?? variant.metafields?.custom?.hidden;
      return raw === true || String(raw || '').toLowerCase() === 'true';
    }

    async #enrichVariantData(products) {
      const enriched = await Promise.all(products.map(product => this.#withVariantData(product)));
      return enriched.filter(Boolean);
    }

    async #withVariantData(product) {
      if (!product?.handle) return product;
      const variants = await this.#loadVisibleVariants(product);
      if (!Array.isArray(variants)) return product;
      return { ...product, _mcuVariants: variants };
    }

    async #loadVisibleVariants(product) {
      const cacheKey = product.handle || String(product.id);
      if (this.#variantCache.has(cacheKey)) return this.#variantCache.get(cacheKey);

      try {
        const url = new URL(product.url || ('/products/' + product.handle), window.location.origin);
        url.searchParams.set('section_id', 'mc-cart-upsell-variants');
        const res = await fetch(url.toString(), { signal: this.#controller?.signal });
        if (!res.ok) return null;
        const data = this.#parseVariantResponse(await res.text());
        const variants = Array.isArray(data?.variants) ? data.variants : null;
        this.#variantCache.set(cacheKey, variants);
        return variants;
      } catch (e) {
        if (e.name === 'AbortError') throw e;
        return null;
      }
    }

    #parseVariantResponse(text) {
      try {
        return JSON.parse(text);
      } catch {}

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const script = doc.querySelector('[data-mcu-variant-json]');
      if (!script?.textContent) return null;
      try {
        return JSON.parse(script.textContent);
      } catch {
        return null;
      }
    }

    #sourceScore(source) {
      if (source === 'complementary') return 90;
      if (source === 'related') return 48;
      if (source === 'recommendations') return 42;
      return 18;
    }

    #sourceRank(source) {
      if (source === 'complementary') return 0;
      if (source === 'related' || source === 'recommendations') return 1;
      return 2;
    }

    #saveSnapshot(addedProductId, options = {}) {
      if (!this.featuredListEl || !this.listEl) return;
      window.__mcCartUpsellSnapshot = {
        createdAt: Date.now(),
        limit: this.limit,
        intent: this.intent,
        fallbackHandles: this.fallbackHandles.join(','),
        seedId: this.#lastSeedId,
        addedProductId,
        persistAdded: Boolean(options.persistAdded),
        stableAddedProductIds: Array.from(this.#stableAddedProductIds),
        featuredHTML: this.featuredListEl.innerHTML,
        moreHTML: this.listEl.innerHTML,
        moreScrollLeft: this.listEl.scrollLeft,
      };
    }

    #restoreFromSnapshot() {
      const snapshot = window.__mcCartUpsellSnapshot;
      if (!snapshot || Date.now() - snapshot.createdAt > 5000) return false;
      if (snapshot.limit !== this.limit || snapshot.intent !== this.intent) return false;
      if (snapshot.fallbackHandles !== this.fallbackHandles.join(',')) return false;

      this.#lastSeedId = snapshot.seedId || null;
      if (Array.isArray(snapshot.stableAddedProductIds)) {
        snapshot.stableAddedProductIds.forEach(id => this.#rememberStableAddedProduct(id));
      }
      this.featuredListEl.innerHTML = snapshot.featuredHTML || '';
      this.listEl.innerHTML = snapshot.moreHTML || '';
      this.#bindCardButtons();

      if (snapshot.addedProductId) {
        this.querySelectorAll('[data-mcu-card]').forEach(card => {
          if (card instanceof HTMLElement && card.dataset.productId === String(snapshot.addedProductId)) {
            if (snapshot.persistAdded) {
              this.#markCardAdded(card, card.querySelector('.mc-cart-upsell__add'), { persist: true });
            } else {
              this.#removeCard(card, false);
            }
          }
        });
      }

      this.#applyStableAddedState();
      this.#syncVisibility();
      requestAnimationFrame(() => {
        this.listEl.scrollLeft = snapshot.moreScrollLeft || 0;
        this.#updateNav();
      });
      return this.#hasCards();
    }

    #variantPriceCents(variant) {
      const raw = variant?.price;
      if (raw === null || raw === undefined || raw === '') return 0;
      if (typeof raw === 'number') return raw;
      const value = String(raw);
      if (value.includes('.')) return Math.round(parseFloat(value) * 100) || 0;
      return parseInt(value, 10) || 0;
    }

    /* -- Carousel nav ------------------------------------------------ */
    #scrollByCards(n) {
      if (!this.listEl) return;
      const card = this.listEl.querySelector('[data-mcu-card]');
      if (!card) return;
      const styles = getComputedStyle(this.listEl);
      const gap = parseFloat(styles.columnGap || styles.gap || '10') || 10;
      const step = card.offsetWidth + gap;
      this.listEl.scrollBy({ left: n * step, behavior: 'smooth' });
    }

    #updateNav() {
      if (!this.navEl || !this.listEl || !this.prevBtn || !this.nextBtn) return;
      const hasOverflow = this.listEl.scrollWidth > this.listEl.clientWidth + 4;
      if (!hasOverflow) {
        this.navEl.setAttribute('hidden', '');
        return;
      }
      this.navEl.removeAttribute('hidden');
      const atStart = this.listEl.scrollLeft <= 4;
      const atEnd = this.listEl.scrollLeft + this.listEl.clientWidth >= this.listEl.scrollWidth - 4;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
    }

    /* -- helpers ----------------------------------------------------- */
    #hide() { this.setAttribute('hidden', ''); }
    #show() { this.removeAttribute('hidden'); }

    #escape(s) {
      return String(s || '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    #sizedImage(url, width) {
      try {
        return url.replace(/\.(jpg|jpeg|png|webp|avif|gif)(\?|$)/i, '_' + width + 'x.$1$2');
      } catch {
        return url;
      }
    }

    #formatMoney(cents) {
      const n = (parseInt(cents, 10) || 0) / 100;
      return '$' + n.toFixed(2);
    }

    #createCard(product, featured) {
      const variant = this.#getVariant(product);
      if (!variant) return null;
      const visibleVariants = this.#getVisibleVariants(product);
      const img = product.featured_image || (product.images || [])[0] || '';
      const imgUrl = img ? (typeof img === 'string' ? img : (img.src || img.url || '')) : '';
      const compareAt = variant.compare_at_price;
      const variantPrice = this.#variantPriceCents(variant);
      const compareAtPrice = this.#variantPriceCents({ price: compareAt });
      const isOnSale = compareAtPrice && compareAtPrice > variantPrice;
      const card = document.createElement('div');
      card.className = featured ? 'mc-cart-upsell__card mc-cart-upsell__card--featured' : 'mc-cart-upsell__card';
      card.dataset.mcuCard = '';
      card.dataset.productId = String(product.id);
      card.dataset.productTitle = product.title || '';

      const url = product.url || ('/products/' + product.handle);
      const titleEsc = this.#escape(product.title);
      const variantTitle = this.#variantTitle(variant);
      const variantControl = this.#variantControlHTML(product, visibleVariants, variant);
      card.innerHTML = `
        ${featured ? '<span class="mc-cart-upsell__badge">Top add</span>' : ''}
        <a class="mc-cart-upsell__media" href="${this.#escape(url)}" tabindex="-1" aria-hidden="true">
          ${imgUrl ? `<img src="${this.#escape(this.#sizedImage(imgUrl, 280))}" alt="" loading="lazy" decoding="async" width="140" height="140">` : ''}
        </a>
        <a class="mc-cart-upsell__title" href="${this.#escape(url)}">${titleEsc}</a>
        ${variantControl}
        <div class="mc-cart-upsell__footer">
          <span class="mc-cart-upsell__price">
            <span class="mc-cart-upsell__price-current" data-mcu-price>${this.#formatMoney(variantPrice)}</span>
            <span class="mc-cart-upsell__price-compare" data-mcu-compare ${isOnSale ? '' : 'hidden'}>${isOnSale ? this.#formatMoney(compareAtPrice) : ''}</span>
          </span>
          <button class="mc-cart-upsell__add" type="button" data-variant-id="${variant.id}" aria-label="Add ${titleEsc}${variantTitle ? ' - ' + this.#escape(variantTitle) : ''} to cart">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
      `;
      card.querySelector('[data-mcu-variant-select]')?.addEventListener('change', () => {
        this.#syncCardVariant(card, product);
      });
      card.querySelector('.mc-cart-upsell__add').addEventListener('click', (e) => {
        this.#onCardAddClick(e);
      });
      return card;
    }

    #variantControlHTML(product, variants, selectedVariant) {
      const visibleVariants = variants.filter(variant => !this.#isHiddenVariant(variant));
      const meaningfulVariants = visibleVariants.filter(variant => this.#variantTitle(variant));
      if (visibleVariants.length > 1 && meaningfulVariants.length > 0) {
        const options = visibleVariants.map(variant => {
          const title = this.#variantTitle(variant) || 'Default';
          const price = this.#variantPriceCents(variant);
          const compareAtPrice = this.#variantPriceCents({ price: variant.compare_at_price });
          return `
            <option
              value="${variant.id}"
              data-price="${price}"
              data-compare-at="${compareAtPrice || ''}"
              ${variant.available ? '' : 'disabled'}
              ${String(variant.id) === String(selectedVariant.id) ? 'selected' : ''}
            >${this.#escape(title)}${variant.available ? '' : ' - Sold out'}</option>
          `;
        }).join('');
        return `
          <div class="mc-cart-upsell__variant">
            <select class="mc-cart-upsell__variant-select" data-mcu-variant-select aria-label="Choose variant for ${this.#escape(product.title)}">
              ${options}
            </select>
          </div>
        `;
      }

      const title = this.#variantTitle(selectedVariant);
      if (!title) return '';
      return `<p class="mc-cart-upsell__variant mc-cart-upsell__variant-name">${this.#escape(title)}</p>`;
    }

    #variantTitle(variant) {
      const title = String(variant?.title || '').trim();
      if (!title || title.toLowerCase() === 'default title') return '';
      return title;
    }

    #syncCardVariant(card, product = null) {
      const select = card.querySelector('[data-mcu-variant-select]');
      const option = select?.selectedOptions?.[0];
      const button = card.querySelector('.mc-cart-upsell__add');
      const priceEl = card.querySelector('[data-mcu-price]');
      const compareEl = card.querySelector('[data-mcu-compare]');
      if (!option || !button) return;

      const variantId = option.value;
      const price = parseInt(option.dataset.price || '0', 10) || 0;
      const compareAt = parseInt(option.dataset.compareAt || '0', 10) || 0;
      const variantTitle = option.textContent.replace(/\s+-\s+Sold out\s*$/i, '').trim();
      const productTitle = product?.title || card.dataset.productTitle || 'item';

      button.dataset.variantId = variantId;
      button.setAttribute(
        'aria-label',
        'Add ' + productTitle + (variantTitle ? ' - ' + variantTitle : '') + ' to cart'
      );
      if (priceEl) priceEl.textContent = this.#formatMoney(price);
      if (compareEl) {
        if (compareAt && compareAt > price) {
          compareEl.textContent = this.#formatMoney(compareAt);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.setAttribute('hidden', '');
        }
      }
    }

    #bindCardButtons() {
      this.querySelectorAll('[data-mcu-card]').forEach((card) => {
        card.querySelector('[data-mcu-variant-select]')?.addEventListener('change', () => {
          this.#syncCardVariant(card);
        });
        card.querySelector('.mc-cart-upsell__add')?.addEventListener('click', (e) => {
          this.#onCardAddClick(e);
        });
      });
    }

    #hydrateStableAddedIds() {
      if (!Array.isArray(window.__mcCartUpsellStableAddedIds)) return;
      window.__mcCartUpsellStableAddedIds.forEach(id => {
        if (id !== null && id !== undefined && id !== '') this.#stableAddedProductIds.add(String(id));
      });
    }

    #rememberStableAddedProduct(productId) {
      if (productId === null || productId === undefined || productId === '') return;
      const id = String(productId);
      this.#stableAddedProductIds.add(id);
      const globalIds = new Set(Array.isArray(window.__mcCartUpsellStableAddedIds) ? window.__mcCartUpsellStableAddedIds.map(String) : []);
      globalIds.add(id);
      window.__mcCartUpsellStableAddedIds = Array.from(globalIds).slice(-12);
    }

    #forgetStableAddedProduct(productId) {
      if (productId === null || productId === undefined || productId === '') return;
      const id = String(productId);
      this.#stableAddedProductIds.delete(id);
      const globalIds = new Set(Array.isArray(window.__mcCartUpsellStableAddedIds) ? window.__mcCartUpsellStableAddedIds.map(String) : []);
      globalIds.delete(id);
      window.__mcCartUpsellStableAddedIds = Array.from(globalIds);
    }

    #applyStableAddedState() {
      if (!this.#stableAddedProductIds.size) return;
      this.querySelectorAll('[data-mcu-card]').forEach(card => {
        if (!(card instanceof HTMLElement)) return;
        if (!this.#stableAddedProductIds.has(String(card.dataset.productId))) return;
        this.#markCardAdded(card, card.querySelector('.mc-cart-upsell__add'), { persist: true });
      });
    }

    #onCardAddClick(e) {
      e.preventDefault();
      e.stopPropagation();
      const btn = e.currentTarget;
      if (!(btn instanceof HTMLElement)) return;
      const id = btn.dataset.variantId;
      if (!id) return;
      const featured = Boolean(btn.closest('[data-mcu-featured-list]'));
      this.#handleAdd(e, {
        id,
        source: featured ? 'cart-drawer-top-add' : 'cart-drawer-addons',
        stable: featured
      });
    }

    #removeCard(card, animate) {
      if (!(card instanceof HTMLElement)) return;
      const remove = () => {
        card.remove();
        this.#syncVisibility();
        this.#updateNav();
      };
      if (!animate) {
        remove();
        return;
      }
      card.classList.add('mc-cart-upsell__card--removing');
      setTimeout(remove, 180);
    }

    #setButtonIcon(btn, state) {
      if (state === 'success') {
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
        return;
      }
      if (state === 'add') {
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      }
    }

    #hasCards() {
      return Boolean(
        (this.featuredListEl && this.featuredListEl.children.length > 0) ||
        (this.listEl && this.listEl.children.length > 0)
      );
    }
  }

  customElements.define('mc-cart-upsell', McCartUpsell);
})();
